import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Injector, NgModule, inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { BrowserModule } from "@angular/platform-browser";
import { RouterModule } from "@angular/router";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { AccountsPayableModule } from "@tylertech/accounts-payable-module";
import { AggregateService, AggregatesModule } from "@tylertech/aggregates-module";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartingModule } from "@tylertech/charting-module";
import { CommonComponentsModule } from "@tylertech/common-components-module";
import { CommonDialogsModule, CommonDialogsService, IntentActionDialogData } from "@tylertech/common-dialogs-module";
import { AppOutletComponent, CoreModule, FileService, isHttpResponseError, ToastService } from "@tylertech/core-module";
import { CustomViewModule } from "@tylertech/custom-view";
import { ForgeBannerModule, ForgeButtonModule, ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeMenuModule, ForgeSkeletonModule, ForgeSkipLinkModule, ForgeToolbarModule, ForgeTooltipModule, ForgeViewModule, ForgeViewSwitcherModule } from "@tylertech/forge-angular";
import { GeneralLedgerModule } from "@tylertech/general-ledger-module";
import { IncodeModule } from "@tylertech/incode-module";
import { InfoCardDialogService, InfoCardModule } from "@tylertech/info-card-module";
import { IntentService, IntentsModule } from "@tylertech/intents-module";
import { InventoryModule } from "@tylertech/inventory-module";
import { LgdTypeAheadModule } from "@tylertech/lgd-reactive-forms";
import { MetadataModule } from "@tylertech/metadata-module";
import { OdataModule } from "@tylertech/odata-module";
import { PacketsModule } from "@tylertech/packets-module";
import { PersonnelModule } from "@tylertech/personnel-module";
import { ProjectAccountingModule } from "@tylertech/project-accounting-module";
import { PurchasingDialogsService, PurchasingModule } from "@tylertech/purchasing-module";
import { ReportDeliveryService, ReportingModule } from "@tylertech/reporting-module";
import { StudentActivitiesModule } from "@tylertech/student-activities-module";
import { ExecutionResult, ICardData, IDocument, IDocumentCriteria, IInfoCardData, IIntent, ISearchCriteria, PerformActionParams, } from "@tylertech/tyler-api-types";
import { UtilityBillingModule } from "@tylertech/utility-billing-module";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { DashboardChartComponent } from "./components/dashboard/dashboardChart.component";
import { DashboardMetadataComponent } from "./components/dashboard/dashboardMetadata.component";
import { EmailTestCardComponent } from "./components/dashboard/emailTestCard.component";
import { UserLookupCardComponent } from "./components/dashboard/userLookup.component";
import { WidgetComponent } from "./components/dashboard/widget.component";
import { WidgetBarComponent } from "./components/dashboard/widgetBar.component";
import { InBalanceChartComponent, InBalanceCheckComponent, InBalanceComponent } from "./components/inBalance";
import { ProcessStepComponent } from "./components/processes/processStep.component";
import { ProcessesComponent } from "./components/processes/processes.component";
import { ProcessingCardComponent } from "./components/processes/processingCard.component";
import { DashboardRoutingModule } from "./dashboardRoutingModule";
import { DashboardMetadataService, InBalanceManagerService, InBalanceService } from "./services";

const angularModules = [
    BrowserModule,
    CommonModule,
    FlexLayoutModule,
    FlexModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
];

const angularMaterialModules = [
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatIconModule
];

const forgeModules = [
    ForgeBannerModule,
    ForgeButtonModule,
    ForgeCardModule,
    ForgeIconButtonModule,
    ForgeIconModule,
    ForgeMenuModule,
    ForgeSkeletonModule,
    ForgeSkipLinkModule,
    ForgeToolbarModule,
    ForgeTooltipModule,
    ForgeViewModule,
    ForgeViewSwitcherModule,
];

const lgdModules = [
    AccountsPayableModule,
    AggregatesModule,
    ChartingModule,
    ChartJsContainerComponent,
    CommonComponentsModule,
    CommonDialogsModule,
    CoreModule,
    CustomViewModule.withComponents(
        /* eslint-disable @typescript-eslint/naming-convention */
        {
            "erp-pro-in-balance-card": InBalanceComponent,
        }
        /* eslint-enable @typescript-eslint/naming-convention */
    ),
    GeneralLedgerModule,
    IncodeModule,
    InfoCardModule,
    IntentsModule,
    InventoryModule,
    LgdTypeAheadModule,
    MetadataModule,
    OdataModule,
    PacketsModule,
    PersonnelModule,
    ProjectAccountingModule,
    PurchasingModule,
    ReportingModule,
    StudentActivitiesModule,
    UtilityBillingModule,
];

const entryComponents = [
    DashboardComponent,
    DashboardChartComponent,
    EmailTestCardComponent,
    DashboardMetadataComponent,
    InBalanceChartComponent,
    InBalanceCheckComponent,
    InBalanceComponent,
    ProcessStepComponent,
    ProcessesComponent,
    ProcessingCardComponent,
    UserLookupCardComponent,
    WidgetBarComponent,
    WidgetComponent,
];

@NgModule({
    imports: [
        ...angularModules,
        ...angularMaterialModules,
        ...forgeModules,
        ...lgdModules,
        DashboardRoutingModule,
    ],
    declarations: entryComponents,
    providers: [
        DashboardMetadataService,
        InBalanceManagerService,
        InBalanceService,
    ],
    bootstrap: [AppOutletComponent],
})
export class DashboardModule
{
    private readonly $aggregateService = inject(AggregateService);
    private readonly infoCardDialogService = inject(InfoCardDialogService);
    private readonly purchasingDialogsService = inject(PurchasingDialogsService);
    private readonly commonDialogsService = inject(CommonDialogsService);
    private readonly $intentService = inject(IntentService);
    private readonly $toastService = inject(ToastService);
    private readonly $reportDeliveryService = inject(ReportDeliveryService);

    constructor()
    {
        const $toastService = this.$toastService;
        const injector = inject(Injector);

        const $fileService = injector.get(FileService);


        // FIXME - we should be registering things like this somewhere else.
        this.$intentService.registerFrontEndAction(
            "smartcard", "comment", "comment", (data?: { readonly Label: string, readonly NextIntent?: IIntent; }) =>
        {
            return this.commonDialogsService.showComment(data?.Label, data?.NextIntent);
        });

        this.$intentService.registerFrontEndAction<ISearchCriteria<IInfoCardData>, void>(
            "smartcard", "launchInfoCardSearchDialog", "infoCardSearchDialog", data =>
        {
            return this.infoCardDialogService.showSearchDialog(data || {});
        });

        // TODO - this should be registered by the PurchasingModule, not here....
        // REVIEW - Does anything even use this intent?
        this.$intentService.registerFrontEndAction(
            "infocard", "launchBidQuoteDialog", "infoCardBidQuoteDialog", (data: Partial<Record<"id", string>>) =>
        {
            return this.purchasingDialogsService.showBidQuotes(null, false, true, data?.id);
        });

        this.$intentService.registerFrontEndAction(
            "smartcard", "intentactiondialog", "web", (data: IntentActionDialogData) =>
        {
            return this.commonDialogsService.showIntentAction(data ?? {});
        });

        this.$intentService.registerFrontEndAction(
            "smartcard", "action", "express", (data: Record<"CardData", ICardData>) =>
        {
            const self = this;
            const cardData: ICardData = data.CardData;
            const crit = <PerformActionParams>{
                CardProviderId: cardData.CardProviderId,
                CardAction: data["CardAction"],
                Card: cardData,
            };
            return this.$aggregateService.PerformCardAction(crit).then(
                (result: ExecutionResult<ICardData>) => result,
                (err: HttpErrorResponse) =>
                {
                    if (err && err.error && err.error.Message)
                        self.$toastService.showToast(err.error.Message);
                    else self.$toastService.showToast("Failed to perform card action");
                });
        });

        this.$intentService.registerFrontEndAction(
            "smartcard", "print", "express", (data: Record<"DocCrit", IDocumentCriteria>) =>
        {
            const criteria: IDocumentCriteria = data.DocCrit;

            return this.$reportDeliveryService.GenerateAndView(criteria)
                .then(
                    (document: IDocument) =>
                    {
                        $fileService.openDocumentLink(document.Content, document.MimeType, document.Filename);
                    },
                    (err: unknown) =>
                    {
                        if (isHttpResponseError(err))
                        {
                            this.$toastService.showToast(err.error.Message);
                        }
                        else
                        {
                            console.error(err);
                        }
                    });
        });
    }
}
